import { useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { t, tEn } from '../i18n/translations'
import { startTrial, trialErrorMessage } from '../lib/trial'

// The Premium row in Settings. Exactly one of five states, so the row always
// says something true rather than offering a trial it cannot grant:
//
//   lifetime teacher   -> handled by the teacher row below; nothing here
//   trial running      -> days remaining
//   subscriber         -> Premium, no trial offered   (requirement 8)
//   eligible           -> the Start button
//   already used       -> why it is gone, not a dead button   (decision C)
export default function TrialSettingsRow() {
  const {
    user, login, isPremium, hasLifetimeTeacherAccess,
    canStartTrial, trialUsed, onFreeTrial, premiumDaysRemaining, refreshTrialStatus,
  } = useAuth()
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState('')

  // A teacher's lifetime access is reported by the teacher row below.
  if (hasLifetimeTeacherAccess) return null

  async function activate() {
    if (starting) return
    setStarting(true)
    setError('')
    try {
      await startTrial()
      await refreshTrialStatus()
    } catch (err) {
      setError(trialErrorMessage(err))
    }
    setStarting(false)
  }

  if (onFreeTrial && premiumDaysRemaining !== null) {
    return (
      <div className="settings-row">
        <span className="settings-label">Premium</span>
        <span className="settings-value settings-trial" title={tEn('trialBadge')}>
          {premiumDaysRemaining === 1
            ? t('trialLastDay')
            : `${t('trialBadge')} — ${premiumDaysRemaining} ${t('trialDaysRemaining')}`}
        </span>
      </div>
    )
  }

  if (isPremium) {
    return (
      <div className="settings-row">
        <span className="settings-label">Premium</span>
        <span className="settings-value settings-premium" title={tEn('premiumActive')}>{t('premiumActive')}</span>
      </div>
    )
  }

  if (!user) {
    return (
      <button className="settings-row settings-credits-btn" onClick={login}>
        <span className="settings-label" title={tEn('trialStart')}>{t('trialStart')}</span>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6"/>
        </svg>
      </button>
    )
  }

  if (trialUsed) {
    return (
      <div className="settings-row">
        <span className="settings-label">Premium</span>
        <span className="settings-value settings-trial-used" title={tEn('trialUsed')}>{t('trialUsed')}</span>
      </div>
    )
  }

  if (!canStartTrial) return null

  return (
    <>
      <button className="settings-row settings-credits-btn" onClick={activate} disabled={starting}>
        <span className="settings-label" title={tEn('trialStart')}>
          {starting ? t('trialStarting') : t('trialStart')}
        </span>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6"/>
        </svg>
      </button>
      {error && <p className="teacher-code-error" role="alert">{error}</p>}
    </>
  )
}
